import { Heading } from '@mm/components';
import {
  ArrowStatefulIcon,
  CloseCircleIcon,
  IconClickEvent,
  ResetIcon,
} from '@mm/icons';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { useTagsCRUDActions } from '@mm/state/actions/TagsCRUDActions';
import { FilterItem } from '@mm/state/model/AudiencesModel';
import { tagsOfKinds } from '@mm/state/selectors/tags';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useActor } from '@xstate/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useContextSelector } from 'use-context-selector';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { AudienceFilterItemInput } from '../../../../../../common/components/AudienceFilterItemInput/AudienceFilterItemInput';
import FilterableSelect from '../../../../../../common/components/FilterableSelect/FilterableSelect';
import { LiveChatContext } from '../../../LiveChatContext';
import './AdvancedFilters.scss';

export const AdvancedFilters = () => {
  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const [lcMachineState, lcMachineSend] = useActor(lcService);

  const {
    remoteUserId,
    selectedAttribute: appliedAttribute,
    selectedTags: appliedTags,
  } = lcMachineState.context || {};

  const [selectedTags, setSelectedTags] = useState(appliedTags);
  const { getAll } = useTagsCRUDActions();

  const botId = useSelector(activeChatbotIdSelector);
  const availableTags = useSelector((state: StoreState) =>
    tagsOfKinds(state, ['regular']),
  );
  const variables = useSelector(
    (state: StoreState) => state.audiences.variables,
  );
  const activeFacebookPageId = useSelector(activeFacebookPageIdSelector);

  useEffect(() => {
    getAll(activeFacebookPageId, false, ['regular']);
  }, []);

  const appliedVariable = variables.find(
    v =>
      v.variableId === appliedAttribute?.variableId &&
      v.variableType === appliedAttribute.variableType,
  );
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);
  const [searchTagQuery, setSearchTagQuery] = useState('');
  const { getAudienceVariable } = useAudiencesCRUDActions();
  useEffect(() => {
    if (variables.length === 0) {
      getAudienceVariable(botId);
    }
  }, [variables]);

  const blankFilterItem: FilterItem = {
    filterType: '',
    operator: 'equal',
    value: '',
    variableId: 0,
    variableType: 'SystemVariable',
    variable: '',
  };

  const [attributeFilter, setAttributeFilter] =
    useState<FilterItem>(blankFilterItem);

  useEffect(() => {
    if (appliedAttribute && appliedVariable) {
      setAttributeFilter({
        filterType: '',
        operator: appliedAttribute.operator,
        value: appliedAttribute.value,
        variableId: appliedAttribute.variableId,
        variableType: appliedAttribute.variableType,
        variable: appliedVariable.name,
      });
    }
  }, [variables, appliedAttribute, appliedVariable]);
  const onClickApply = () => {
    const viewUpdate = {
      selectedTags,
      selectedAttribute:
        attributeFilter?.operator &&
        attributeFilter.value &&
        attributeFilter.variableId
          ? {
              variableType: attributeFilter.variableType,
              variableId: attributeFilter.variableId,
              operator: attributeFilter.operator,
              value: attributeFilter.value,
            }
          : null,
    };

    lcMachineSend({
      type: 'LC_VIEW_UPDATE',
      payload: { viewUpdate },
    });

    if (remoteUserId) {
      lcMachineSend({ type: 'LC_REMOTE_USER_ID_CHANGE', payload: undefined });
    }
  };

  const onClickReset = () => {
    setSelectedTags([]);
    setAttributeFilter(blankFilterItem);

    const viewUpdate = {
      selectedTags: [],
      selectedAttribute: null,
    };

    lcMachineSend({
      type: 'LC_VIEW_UPDATE',
      payload: { viewUpdate },
    });

    if (remoteUserId) {
      lcMachineSend({ type: 'LC_REMOTE_USER_ID_CHANGE', payload: undefined });
    }
  };

  const onTagAdd = (tag: string) => {
    if (tag) {
      setSelectedTags([...selectedTags, tag]);
      setIsTagsDropdownOpen(!isTagsDropdownOpen);
    }
  };
  const onTagRemove = (e: IconClickEvent, tag: string) => {
    e.stopPropagation();
    setSelectedTags(selectedTags.filter(t => t !== tag));
  };

  const tagSuggestions = useMemo(() => {
    return [
      {
        title: '',
        suggestions: selectedTags.length
          ? availableTags.filter(
              availableTag =>
                !selectedTags.some(tag => tag === availableTag.name),
            )
          : availableTags,
      },
    ];
  }, [availableTags, selectedTags]);

  const filtersValid =
    !attributeFilter.variableId ||
    !!(
      attributeFilter.variableId &&
      attributeFilter.operator &&
      attributeFilter.value.length
    );

  return (
    <div className="advanced-filters__container">
      <div className="advanced-filters__header">
        <Heading size={4} text="Advanced Filters" />
        <div className="advanced-filters__header-reset" onClick={onClickReset}>
          <ResetIcon className="advanced-filters__header-reset-icon" /> Reset
        </div>
      </div>
      <div>
        <div className="advanced-filters__filter-container">
          <span className="advanced-filters__filter-name">Tags</span>
          <div
            onClick={() => setIsTagsDropdownOpen(o => !o)}
            className="advanced-filters__tags-input"
          >
            <span className="advanced-filters__tags-arrow">
              <ArrowStatefulIcon
                direction={isTagsDropdownOpen ? 'up' : 'down'}
              />
            </span>
            {selectedTags.map(tag => {
              return (
                <div className="advanced-filters__tag" key={tag}>
                  <div>{tag}</div>

                  <CloseCircleIcon
                    className="advanced-filters__tag-remove"
                    onClick={e => onTagRemove(e, tag)}
                    color="#009EFF"
                  />
                </div>
              );
            })}
          </div>
        </div>
        {isTagsDropdownOpen && (
          <FilterableSelect
            className="advanced-filters__tag-select"
            showSuggestOnly={true}
            defaultValue={searchTagQuery}
            onBlur={() => setIsTagsDropdownOpen(false)}
            onInput={e => setSearchTagQuery(e.currentTarget.value)}
            onChange={(_id, suggestion) => onTagAdd(suggestion?.name || '')}
            name="filterable"
            searchPlaceholder="Add tag"
            suggestions={tagSuggestions}
          />
        )}
      </div>
      <AudienceFilterItemInput
        className="advanced-filters__attribute-filter"
        filter={attributeFilter}
        setFilter={setAttributeFilter}
        hideOperatorAndValueUntilVariableSelected
        showBlankVariableOption
      />

      <div className="advanced-filters__filter-container">
        <PrimaryButton
          color={ButtonColor.secondaryBlue}
          onClick={onClickApply}
          disabled={!filtersValid}
        >
          Apply
        </PrimaryButton>
      </div>
    </div>
  );
};
